/**
 * Lazy phone-collection modal.
 *
 * Two steps: enter phone -> backend sends an OTP; enter OTP -> backend verifies
 * and attaches the phone to the authenticated account. On success the store's
 * user.phone is updated so phone-required actions unblock.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
	collectPhone,
	verifyCollectedPhone,
} from "@/features/auth/services/auth.service";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { FloatPanel, StudioButton } from "@/features/studio/theme";

interface CollectPhoneModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initialPhone?: string;
}

function messageFrom(err: unknown, fallback: string): string {
	const e = err as { response?: { data?: { message?: string } } };
	return e.response?.data?.message || fallback;
}

export function CollectPhoneModal({
	open,
	onOpenChange,
	initialPhone = "",
}: CollectPhoneModalProps) {
	const setPhone = useAuthStore((s) => s.setPhone);
	const [step, setStep] = useState<"phone" | "otp">("phone");
	const [phone, setPhoneInput] = useState(initialPhone);
	const [otp, setOtp] = useState("");
	const [loading, setLoading] = useState(false);

	const reset = () => {
		setStep("phone");
		setPhoneInput(initialPhone);
		setOtp("");
	};

	useEffect(() => {
		if (open) {
			setPhoneInput(initialPhone);
			setStep("phone");
			setOtp("");
		}
	}, [open, initialPhone]);

	const handleSend = async (e: React.FormEvent) => {
		e.preventDefault();
		const clean = phone.replace(/\D/g, "");
		if (clean.length !== 10) {
			toast.error("Enter a valid 10-digit phone number");
			return;
		}
		const cleanInitial = initialPhone.replace(/\D/g, "");
		if (clean === cleanInitial) {
			toast.error("This is already your active phone number");
			return;
		}
		setLoading(true);
		try {
			const res = await collectPhone(clean);
			if (!res.success) {
				toast.error(res.message || "Could not send the code");
				return;
			}
			setStep("otp");
			toast.success("Code sent");
		} catch (err) {
			toast.error(messageFrom(err, "Could not send the code"));
		} finally {
			setLoading(false);
		}
	};

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		const clean = phone.replace(/\D/g, "");
		setLoading(true);
		try {
			const res = await verifyCollectedPhone(clean, otp);
			if (!res.success) {
				toast.error(res.message || "Invalid code");
				return;
			}
			setPhone(res.data.phone ?? clean);
			toast.success("Phone number added");
			reset();
			onOpenChange(false);
		} catch (err) {
			toast.error(messageFrom(err, "Invalid code"));
		} finally {
			setLoading(false);
		}
	};

	if (!open) return null;

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click dismiss does not require keyboard trigger
		// biome-ignore lint/a11y/noStaticElementInteractions: backdrop backdrop-blur overlay
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				background:
					"color-mix(in srgb, var(--color-dashboard-shadow-ink) 40%, transparent)",
				backdropFilter: "blur(6px)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 999,
				padding: 16,
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget) {
					reset();
					onOpenChange(false);
				}
			}}
		>
			<FloatPanel
				elevation={2}
				padding={24}
				style={{ width: "100%", maxWidth: 420 }}
			>
				<div className="space-y-4">
					<div
						style={{
							borderBottom: "1px solid var(--color-dashboard-border-soft)",
							paddingBottom: 12,
						}}
					>
						<h3
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontWeight: 600,
								fontSize: 18,
								color: "var(--color-dashboard-text)",
								margin: 0,
							}}
						>
							Add your phone number
						</h3>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 13,
								color: "var(--color-dashboard-muted)",
								marginTop: 4,
								margin: 0,
							}}
						>
							We'll text a one-time code to verify it.
						</p>
					</div>

					{step === "phone" ? (
						<form onSubmit={handleSend} className="space-y-4">
							<div className="space-y-1.5">
								<label
									htmlFor="collect-phone"
									style={{
										fontSize: 13,
										fontWeight: 500,
										color: "var(--color-dashboard-muted-dark)",
										display: "block",
									}}
								>
									Phone number
								</label>
								<div
									style={{
										display: "flex",
										borderRadius: 7,
										overflow: "hidden",
										border: "1px solid var(--color-border-default)",
									}}
								>
									<span
										style={{
											display: "inline-flex",
											alignItems: "center",
											background: "var(--color-dashboard-divider)",
											borderRight: "1px solid var(--color-border-default)",
											padding: "0 14px",
											color: "var(--color-dashboard-muted)",
											fontSize: 14,
											fontFamily: "var(--font-mono)",
										}}
									>
										+91
									</span>
									<input
										id="collect-phone"
										type="tel"
										value={phone}
										onChange={(e) =>
											setPhoneInput(
												e.target.value.replace(/\D/g, "").slice(0, 10),
											)
										}
										placeholder="9876543210"
										style={{
											flex: 1,
											minWidth: 0,
											padding: "10px 14px",
											background: "var(--color-dashboard-paper)",
											fontSize: 14,
											fontFamily: "var(--font-mono)",
											outline: "none",
											border: "none",
										}}
										maxLength={10}
										required
									/>
								</div>
							</div>
							<div
								style={{
									display: "flex",
									gap: 12,
									justifyContent: "flex-end",
									paddingTop: 12,
								}}
							>
								<StudioButton
									type="button"
									variant="ghost"
									onClick={() => {
										reset();
										onOpenChange(false);
									}}
								>
									Cancel
								</StudioButton>
								<StudioButton
									type="submit"
									variant="primary"
									disabled={loading || phone.length !== 10}
								>
									{loading ? "Sending..." : "Send code"}
								</StudioButton>
							</div>
						</form>
					) : (
						<form onSubmit={handleVerify} className="space-y-4">
							<div className="space-y-1.5">
								<label
									htmlFor="collect-otp"
									style={{
										fontSize: 13,
										fontWeight: 500,
										color: "var(--color-dashboard-muted-dark)",
										display: "block",
									}}
								>
									Enter the 6-digit code
								</label>
								<input
									id="collect-otp"
									inputMode="numeric"
									value={otp}
									onChange={(e) =>
										setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
									}
									placeholder="123456"
									style={{
										width: "100%",
										padding: "10px 14px",
										background: "var(--color-dashboard-paper)",
										border: "1px solid var(--color-border-default)",
										borderRadius: 7,
										fontSize: 14,
										fontFamily: "var(--font-mono)",
										outline: "none",
									}}
									maxLength={6}
									required
								/>
							</div>
							<div
								style={{
									display: "flex",
									gap: 12,
									justifyContent: "flex-end",
									paddingTop: 12,
								}}
							>
								<StudioButton
									type="button"
									variant="ghost"
									onClick={() => setStep("phone")}
								>
									Back
								</StudioButton>
								<StudioButton
									type="submit"
									variant="primary"
									disabled={loading || otp.length !== 6}
								>
									{loading ? "Verifying..." : "Verify & add"}
								</StudioButton>
							</div>
						</form>
					)}
				</div>
			</FloatPanel>
		</div>
	);
}
